import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { updateParent } from '../../../lib/modal';
import { formStyles } from '../../../constants/SiteVariables';
import { submitApplication } from '../../../actions/applications';
import { getCompany } from '../../../actions/companies';
import { getRoute, loading } from '../../../actions/sessions';
import { asyncActions } from '../../../actions/general';
import { confirmShoppingCart, getLoanAgreement } from '../../../actions/shopping_carts';
import useRouteStatus from '../../../hooks/useRouteStatus';
import useSessionEntity from '../../../hooks/useSessionEntity';
import { formatCurrency } from '../../../lib/formatters';
import { emitEvent } from '../../../lib/modal';
import Loading from '../../shared/Loading';
import Error from '../../shared/Error';

const numPayments = {
  2: { term: 'TWO', period: 'monthly' },
  3: { term: 'THREE', period: 'monthly' },
  4: { term: 'FOUR', period: 'biweekly' },
  6: { term: 'SIX', period: 'monthly' },
  9: { term: 'NINE', period: 'monthly' },
  12: { term: 'TWELVE', period: 'monthly' },
  24: { term: 'TWENTY FOUR', period: 'monthly' },
}

export default function Confirm({location}) {
  const dispatch = useDispatch();

  const isLoading = useSelector(state => state.sessions.get('loading'));
  const session = useSelector(state => state.sessions.get('session'));
  const cart = useSessionEntity('shopping_cart');
  const merchant = useSessionEntity('merchant');
  const user = useSessionEntity('user');
  const [loanAgreement, setLoanAgreement] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [valid, setValid] = useState(true);

  useEffect(() => {
    dispatch(loading(true));
    dispatch(asyncActions(
      getLoanAgreement(cart)
    ))
    .then(res => setLoanAgreement({ __html: res.attributes.data }))
    .then(() => dispatch(loading(false)));
  }, []);

  useEffect(() => {
    if (agreed) setValid(true);
  }, [agreed]);

  const save = e => {
    e.preventDefault();

    if (agreed) {
      emitEvent(user, {
        path: location.pathname,
        selected_term: cart.num_payments
      });

      dispatch(loading(true));
      dispatch(asyncActions(
        confirmShoppingCart(cart, agreed),
        getCompany(session.depId('company')),
        getRoute()
      ))
      .then(() => dispatch(loading(false)));
    } else {
      setValid(false);
    }
  }

  const headerText = () => {
    if (cart.using_virtual_card) {
      return(<>
        Complete your {formatCurrency(cart.grand_total)} order<br/>to get your virtual card.
      </>)
    } else {
      return(<>
        {cart.pending_completion ?
          <>Complete your {formatCurrency(cart.grand_total)} order with <span style={{ whiteSpace: 'nowrap' }}>{merchant.name}</span></>
        :
          <>Complete your order</>
        }
      </>)
    }
  }

  useRouteStatus(location);

  if (!(merchant && cart)) {
    return (<Loading active={true} />);
  }

  return (
    <div id="content">
      <div className="has-text-centered mb-5">
        <div className="is-size-4 has-text-weight-semibold">{headerText()}</div>
      </div>

      <Error />

      <div className="card confirm-card mb-3">
        <div className="card-content has-text-centered">
          {cart.num_payments > 1 && <span className="is-size-4"><strong>{numPayments[cart.num_payments].term}</strong> {numPayments[cart.num_payments].period} payments of <strong>{formatCurrency(cart.payment_amount)}</strong></span>}
          {cart.num_payments === 1 && <span className="is-size-4"><strong>One</strong> single payment of <strong>{formatCurrency(cart.payment_amount)}</strong></span>}

          {cart.num_payments !== 4 && (
            <div className="mt-2">
              First payment due {merchant.active_promo.get('max_net_payment') + 1} days after your order is processed
            </div>)}
          {cart.num_payments === 4 && (
            <div className="mt-2">
              First payment due today
            </div>)}
        </div>

        <div className="columns is-mobile">
          <div className="column has-text-centered left">
            <div className="content">
              TOTAL INTEREST
              <div className="has-text-weight-bold">
                {formatCurrency(cart.approved_terms?.find(t => t.get('term') === cart.num_payments).get('total_fees'))}
              </div>
            </div>
          </div>
          <div className="column is-three-fifths has-text-centered right">
            <div className="content">
              TOTAL LOAN AMOUNT
              <div className="has-text-weight-bold">
                {formatCurrency(cart.total_amount_paid)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="columns is-mobile mb-0">
        <div className="column has-text-centered">
          <p>
            Not what you want? <Link className="has-text-weight-bold" to="/purchase/terms">Select a different term</Link>
          </p>
        </div>
      </div>

      <div className="columns is-mobile mb-0">
        <div className="column has-text-centered">
          <div dangerouslySetInnerHTML={loanAgreement}></div>
        </div>
      </div>

      <div className="columns is-mobile mb-0">
        <div className="column has-text-centered">
          <div className="field">
            <input type="checkbox" className="is-checkradio" id="agree" name="agree" onChange={() => setAgreed(!agreed)} value={agreed} checked={agreed} />
            <label htmlFor="agree">
              I have read and agree to the loan terms and conditions
            </label>
            {!valid && <div className={formStyles.validation}>
              * Required
            </div>}
          </div>
        </div>
      </div>

      <div className="columns">
        <div className="column has-text-centered">
          <button
            onClick={save}
            type="submit"
            role="submit"
            disabled={isLoading}
            className={classNames('button is-primary is-large is-fullwidth has-text-weight-bold', {
              'is-loading': isLoading
            })}>
            {!cart.using_virtual_card ? 'Confirm Loan & Complete Order' : 'Confirm Loan & Get Virtual Card'}
          </button>
        </div>
      </div>
    </div>)
}
